import { type FrameGraphPostProcessTask, type IViewportLike } from "core/index";
import { editableInPropertyPage, PropertyTypeForEdition } from "../../../../Decorators/nodeDecorator";
import { NodeRenderGraphBasePostProcessBlock } from "./basePostProcessBlock";

/**
 * Base class for post process blocks.
 */
export class NodeRenderGraphBaseWithPropertiesPostProcessBlock extends NodeRenderGraphBasePostProcessBlock {
    protected override _frameGraphTask: FrameGraphPostProcessTask;

    protected _useCurrentViewport = false;
    protected _useFullScreenViewport = true;
    protected _viewport: IViewportLike = { x: 0, y: 0, width: 1, height: 1 };

    /** If true, the depth attachment will be read-only. */
    @editableInPropertyPage("Depth Read Only", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get depthReadOnly(): boolean {
        return this._frameGraphTask.depthReadOnly;
    }

    public set depthReadOnly(value: boolean) {
        this._frameGraphTask.depthReadOnly = value;
    }

    /** If true, the stencil attachment will be read-only. */
    @editableInPropertyPage("Stencil Read Only", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get stencilReadOnly(): boolean {
        return this._frameGraphTask.stencilReadOnly;
    }

    public set stencilReadOnly(value: boolean) {
        this._frameGraphTask.stencilReadOnly = value;
    }

    /** If true, color write will be disabled when applying the post process. */
    @editableInPropertyPage("Disable Color Write", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get disableColorWrite(): boolean {
        return this._frameGraphTask.disableColorWrite;
    }

    public set disableColorWrite(value: boolean) {
        this._frameGraphTask.disableColorWrite = value;
    }

    /** If true, the post process will be generated by a back face full-screen quad (CW order). */
    @editableInPropertyPage("Draw Back Face", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get drawBackFace(): boolean {
        return this._frameGraphTask.drawBackFace;
    }

    public set drawBackFace(value: boolean) {
        this._frameGraphTask.drawBackFace = value;
    }

    /** If depth testing should be enabled (default is true). */
    @editableInPropertyPage("Depth Test", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get depthTest(): boolean {
        return this._frameGraphTask.depthTest;
    }

    public set depthTest(value: boolean) {
        this._frameGraphTask.depthTest = value;
    }

    private _setViewport() {
        if (this._useCurrentViewport) {
            this._frameGraphTask.viewport = null;
        } else if (this._useFullScreenViewport) {
            this._frameGraphTask.viewport = undefined;
        } else {
            this._frameGraphTask.viewport = this._viewport;
        }
    }

    /** If true, the current viewport will be left unchanged. */
    @editableInPropertyPage("Use currently active viewport", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get useCurrentViewport(): boolean {
        return this._useCurrentViewport;
    }

    public set useCurrentViewport(value: boolean) {
        this._useCurrentViewport = value;
        this._setViewport();
    }

    /** If true, a full screen viewport will be used. */
    @editableInPropertyPage("Use full screen viewport", PropertyTypeForEdition.Boolean, "BASE PROPERTIES")
    public get useFullScreenViewport(): boolean {
        return this._useFullScreenViewport;
    }

    public set useFullScreenViewport(value: boolean) {
        this._useFullScreenViewport = value;
        this._setViewport();
    }

    /** The viewport to use. */
    @editableInPropertyPage("    Viewport", PropertyTypeForEdition.Viewport, "BASE PROPERTIES")
    public get viewport(): IViewportLike {
        return this._viewport;
    }

    public set viewport(value: IViewportLike) {
        this._viewport = value;
        this._setViewport();
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "NodeRenderGraphBaseWithPropertiesPostProcessBlock";
    }

    protected override _dumpPropertiesCode() {
        const codes: string[] = [];
        codes.push(`${this._codeVariableName}.depthReadOnly = ${this.depthReadOnly};`);
        codes.push(`${this._codeVariableName}.stencilReadOnly = ${this.stencilReadOnly};`);
        codes.push(`${this._codeVariableName}.disableColorWrite = ${this.disableColorWrite};`);
        codes.push(`${this._codeVariableName}.drawBackFace = ${this.drawBackFace};`);
        codes.push(`${this._codeVariableName}.depthTest = ${this.depthTest};`);
        codes.push(`${this._codeVariableName}.viewport = ${this._useCurrentViewport ? "null" : this._useFullScreenViewport ? "undefined" : JSON.stringify(this._viewport)};`);
        return super._dumpPropertiesCode() + codes.join("\n");
    }

    public override serialize(): any {
        const serializationObject = super.serialize();
        serializationObject.depthReadOnly = this.depthReadOnly;
        serializationObject.stencilReadOnly = this.stencilReadOnly;
        serializationObject.disableColorWrite = this.disableColorWrite;
        serializationObject.drawBackFace = this.drawBackFace;
        serializationObject.depthTest = this.depthTest;
        serializationObject.useCurrentViewport = this._useCurrentViewport;
        serializationObject.useFullScreenViewport = this._useFullScreenViewport;
        serializationObject.viewport = this._viewport;
        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);
        this.depthReadOnly = !!serializationObject.depthReadOnly;
        this.stencilReadOnly = !!serializationObject.stencilReadOnly;
        this.disableColorWrite = !!serializationObject.disableColorWrite;
        this.drawBackFace = !!serializationObject.drawBackFace;
        this.depthTest = serializationObject.depthTest ?? true;
        if (serializationObject.useCurrentViewport !== undefined) {
            this._useCurrentViewport = serializationObject.useCurrentViewport;
            this._useFullScreenViewport = serializationObject.useFullScreenViewport;
            this._viewport = serializationObject.viewport;
        }
        this._setViewport();
    }
}
